import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Skeleton, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { makeStyles } from 'tss-react/mui'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { Security } from '../../proto/generated/security'
import { Exchange, exchangeToJSON } from '../../proto/generated/common'
import { ExpiryOpenInterestProto } from '../../proto/generated/open-interest'
import ROUTES from '../../assets/routes'
import { getSecurityKey, isFnOSecurity } from '../../util/security'
import { getEffectiveTradingDate, getPreviousValidTradingDay } from '../../util/tradingCalendar'
import { getBeforeRegularSessionOpenInfo } from '../../util/tradingSessionLive'
import {
    getTradingSessionsForSecurity,
    resolveTradingSessionsFromCalendar,
} from '../../util/tradingSessionSelection'
import {
    selectExchangeTradingSessions,
    selectTradingCalendarDataByExchange,
} from '../../selectors/chart'
import { selectOiStrikeFilterLtp } from '../../selectors/security'
import { selectUnderlyingOptionsExpiriesV2 } from '../../selectors/options'
import { updatePrimarySecurityAsync } from '../../reducer/home.thunks'
import { AppDispatch } from '../../store'
import {
    fetchOiSnapshot,
    oiSnapshotMemoryKey,
    previousTradingDaySessionHours,
    resolveOiWindow,
    resolvePreferredBarExpiryIds,
} from '../../pages/OpenInterestAnalysis/oiAnalysisOiData'
import { buildOiChartData, OiChartRow } from '../../pages/OpenInterestAnalysis/oiChartData'
import { OI_OVERVIEW_BAR_STRIKE_COUNT } from '../../pages/OpenInterestAnalysis/oiOverview/oiOverviewConfig'
import { resolveOverviewExpiryName } from '../../pages/OpenInterestAnalysis/oiOverview/resolveOverviewStrikes'
import OverviewMiniBarChart from '../../pages/OpenInterestAnalysis/oiOverview/OverviewMiniBarChart'
import OverviewSectionHeader from './OverviewSectionHeader'

type Props = {
    security: Security | undefined
}

const CHART_HEIGHT = 170

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: theme.spacing(0, 2, 2),
    },
    chartBlock: {
        '& + &': {
            marginTop: theme.spacing(1.5),
        },
    },
    chartTitle: {
        fontSize: '12px',
        fontWeight: 500,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    },
    chart: {
        width: '100%',
        height: CHART_HEIGHT,
        minHeight: CHART_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
    },
    moreButton: {
        marginTop: theme.spacing(1.5),
        textTransform: 'none',
        fontSize: '12px',
        fontWeight: 500,
        justifyContent: 'space-between',
    },
}))

const OiAnalysisSection = ({ security }: Props) => {
    const { classes } = useStyles()
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()

    const securityKey = security ? getSecurityKey(security.exchange, security.code) : ''
    const strikeFilterLtp = useSelector(selectOiStrikeFilterLtp(securityKey))
    const optionsExpiries = useSelector(selectUnderlyingOptionsExpiriesV2(securityKey))
    const exchangeTradingSessions = useSelector(selectExchangeTradingSessions)
    const calendarEvents = useSelector(
        selectTradingCalendarDataByExchange(security?.exchange ?? Exchange.NSE)
    )

    const [loading, setLoading] = useState(false)
    const [rows, setRows] = useState<ExpiryOpenInterestProto[]>([])

    const [sessionBoundsKey, setSessionBoundsKey] = useState(0)
    useEffect(() => {
        if (!exchangeTradingSessions?.length || security?.exchange == null) return
        const sessions = getTradingSessionsForSecurity(exchangeTradingSessions, security)
        if (!sessions.length) return
        const timeoutIds: ReturnType<typeof setTimeout>[] = []
        const now = Date.now()
        for (const ts of sessions) {
            if (now < ts.startTime) {
                timeoutIds.push(
                    setTimeout(() => setSessionBoundsKey((k) => k + 1), ts.startTime - now + 250)
                )
            }
        }
        return () => timeoutIds.forEach(clearTimeout)
    }, [exchangeTradingSessions, security])

    const { beforeRegularOpen } = useMemo(
        () => getBeforeRegularSessionOpenInfo(exchangeTradingSessions, security, Date.now()),
        [exchangeTradingSessions, security, sessionBoundsKey]
    )

    const effectiveTradingDate = useMemo(
        () => getEffectiveTradingDate(calendarEvents),
        [calendarEvents]
    )

    const analysisDay = useMemo(() => {
        if (!effectiveTradingDate) return dayjs().startOf('day')
        if (beforeRegularOpen && calendarEvents) {
            const previousDay = getPreviousValidTradingDay(new Date(effectiveTradingDate), calendarEvents)
            if (previousDay) return dayjs(previousDay).startOf('day')
        }
        return dayjs(effectiveTradingDate).startOf('day')
    }, [beforeRegularOpen, calendarEvents, effectiveTradingDate])

    const analysisDayKey = analysisDay.format('YYYYMMDD')

    const analysisSessionHours = useMemo(() => {
        const sessions = resolveTradingSessionsFromCalendar(
            calendarEvents,
            analysisDay.toDate(),
            security
        )
        const dayStart = analysisDay.startOf('day')
        return sessions.map((session) => [
            dayStart.add(session?.startTime, 'second').valueOf(),
            dayStart.add(session?.endTime, 'second').valueOf(),
        ])
    }, [calendarEvents, security, analysisDay])

    const previousDaySessionHours = useMemo(
        () =>
            previousTradingDaySessionHours(analysisDay, calendarEvents, (date) => {
                const sessions = resolveTradingSessionsFromCalendar(
                    calendarEvents,
                    date.toDate(),
                    security
                )
                const dayStart = date.startOf('day')
                return sessions.map((session) => [
                    dayStart.add(session?.startTime, 'second').valueOf(),
                    dayStart.add(session?.endTime, 'second').valueOf(),
                ])
            }),
        [analysisDay, calendarEvents, security]
    )

    const securityRef = useRef(security)
    securityRef.current = security
    const sessionHoursRef = useRef(analysisSessionHours)
    sessionHoursRef.current = analysisSessionHours
    const previousDaySessionHoursRef = useRef(previousDaySessionHours)
    previousDaySessionHoursRef.current = previousDaySessionHours
    const tradingDateRef = useRef(effectiveTradingDate)
    tradingDateRef.current = effectiveTradingDate

    useEffect(() => {
        const current = securityRef.current
        if (!current || !securityKey) {
            setRows([])
            setLoading(false)
            return
        }

        let cancelled = false
        setLoading(true)

        ;(async () => {
            try {
                const snapshot = await fetchOiSnapshot({
                    security: { code: current.code, exchange: current.exchange },
                    expiries: [],
                        window: resolveOiWindow({
                            analysisDay,
                            effectiveTradingDate: tradingDateRef.current,
                            sessionHours: sessionHoursRef.current,
                            previousTradingDaySessionHours: [],
                        }),
                    memoryKey: oiSnapshotMemoryKey('oiOverview', `${securityKey}|${analysisDayKey}`),
                })
                if (cancelled) return
                setRows(snapshot)
            } catch (error) {
                if (!cancelled) {
                    console.error('Error fetching sidebar OI analysis data', error)
                    setRows([])
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [securityKey, analysisDayKey, analysisDay])

    const preferredExpiry = useMemo(() => {
        const name = resolveOverviewExpiryName(optionsExpiries)
        return optionsExpiries?.find((o) => o.expiry.name === name)?.expiry
    }, [optionsExpiries])

    const expiryIds = useMemo(
        () => resolvePreferredBarExpiryIds(rows, preferredExpiry),
        [rows, preferredExpiry]
    )

    const buildArgs = useMemo(
        () => ({
            oiChangeData: rows,
            selectedExpiries: expiryIds,
            strikeCount: OI_OVERVIEW_BAR_STRIKE_COUNT,
            strikeFilterLtp: Number(strikeFilterLtp ?? 0),
            priceMultiplier: security?.priceMultiplier ?? 100,
            showOI: false,
        }),
        [rows, expiryIds, strikeFilterLtp, security?.priceMultiplier]
    )

    const oiLevels = useMemo(
        () => buildOiChartData({ ...buildArgs, isOIChangeScreen: false }),
        [buildArgs]
    )
    const oiChange = useMemo(
        () => buildOiChartData({ ...buildArgs, isOIChangeScreen: true }),
        [buildArgs]
    )

    const lastLevelsRef = useRef<OiChartRow[]>([])
    const lastChangeRef = useRef<OiChartRow[]>([])
    useEffect(() => {
        if (oiLevels.length) lastLevelsRef.current = oiLevels
        else if (!loading) lastLevelsRef.current = []
        if (oiChange.length) lastChangeRef.current = oiChange
        else if (!loading) lastChangeRef.current = []
    }, [oiLevels, oiChange, loading])

    const displayLevels = oiLevels.length ? oiLevels : loading ? lastLevelsRef.current : []
    const displayChange = oiChange.length ? oiChange : loading ? lastChangeRef.current : []
    const hasData = displayLevels.length > 0 || displayChange.length > 0

    const handleOpenOverview = useCallback(async () => {
        if (!security?.code) return
        const key = getSecurityKey(security.exchange, security.code)
        const isFnO = isFnOSecurity(security)
        await updatePrimarySecurityAsync({
            key,
            expressionMode: false,
            securities: [key],
            underlyingSecurityCode: getSecurityKey(
                security.exchange,
                isFnO ? security.underlyingSecurityCode! : security.code
            ),
        })(dispatch)
        navigate(
            generatePath(ROUTES.oiOverview, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code,
            })
        )
    }, [dispatch, navigate, security])

    if (!security) return null

    if (!hasData) {
        if (!loading) return null
        return (
            <Box boxShadow={0}>
                <OverviewSectionHeader title="OI Analysis" />
                <Box className={classes.container}>
                    <Skeleton variant="rounded" height={CHART_HEIGHT} sx={{ mb: 1.5 }} />
                    <Skeleton variant="rounded" height={CHART_HEIGHT} />
                </Box>
            </Box>
        )
    }

    return (
        <Box boxShadow={0} sx={{ opacity: loading ? 0.72 : 1, transition: 'opacity 0.2s ease' }}>
            <OverviewSectionHeader title="OI Analysis" />
            <Box className={classes.container}>
                {displayLevels.length > 0 ? (
                    <Box className={classes.chartBlock}>
                        <Typography className={classes.chartTitle}>Open Interest</Typography>
                        <Box className={classes.chart}>
                            <OverviewMiniBarChart data={displayLevels} compactBars />
                        </Box>
                    </Box>
                ) : null}
                {displayChange.length > 0 ? (
                    <Box className={classes.chartBlock}>
                        <Typography className={classes.chartTitle}>OI Change</Typography>
                        <Box className={classes.chart}>
                            <OverviewMiniBarChart data={displayChange} compactBars />
                        </Box>
                    </Box>
                ) : null}
                <Button
                    fullWidth
                    variant="outlined"
                    color="inherit"
                    size="small"
                    className={classes.moreButton}
                    endIcon={<ChevronRightIcon fontSize="small" />}
                    onClick={handleOpenOverview}
                >
                    OI Overview
                </Button>
            </Box>
        </Box>
    )
}

export default memo(OiAnalysisSection)
